import React, { useEffect, useMemo, useState } from "react";
import { audiencesService } from "../../services/audiences";
import { parsePaginatedResponse } from "../../utils/pagination";

const PAGE_SIZE = 100;
const CHART_HEIGHT_PX = 36;
const MIN_BAR_PX = 2;

const startOfLocalDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.getTime();
};

const enumerateLocalDays = (startMs, endMs) => {
  const out = [];
  const cur = new Date(startMs);
  cur.setHours(0, 0, 0, 0);
  const end = new Date(endMs);
  end.setHours(0, 0, 0, 0);
  while (cur.getTime() <= end.getTime()) {
    out.push(cur.getTime());
    cur.setDate(cur.getDate() + 1);
  }
  return out;
};

const formatDayLabel = (dayMs) =>
  new Date(dayMs).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });

const barHeightPx = (count, maxCount) => {
  if (count <= 0) return MIN_BAR_PX;
  if (maxCount <= 0) return MIN_BAR_PX;
  return MIN_BAR_PX + (count / maxCount) * (CHART_HEIGHT_PX - MIN_BAR_PX);
};

const AudienceUploadTimelineHistogram = ({ audienceHash }) => {
  const [buckets, setBuckets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  useEffect(() => {
    if (!audienceHash) return;
    setLoading(true);
    setError(null);
    audiencesService.getAudienceVideos(
      {
        audience_hash: audienceHash,
        page: 1,
        page_size: PAGE_SIZE,
        sort_by: "uploaded_at",
        sort_direction: "desc",
      },
      (res) => {
        const { results } = parsePaginatedResponse(res);
        const uploads = (results || [])
          .map((row) => row.video?.uploaded_at)
          .filter(Boolean)
          .map((iso) => startOfLocalDay(new Date(iso)));

        if (uploads.length === 0) {
          setBuckets([]);
          setLoading(false);
          return;
        }

        const firstDay = Math.min(...uploads);
        const today = startOfLocalDay(new Date());
        const dayKeys = enumerateLocalDays(firstDay, today);
        const countByDay = {};
        for (const k of uploads) {
          countByDay[k] = (countByDay[k] || 0) + 1;
        }
        setBuckets(
          dayKeys.map((k) => ({
            dayMs: k,
            count: countByDay[k] || 0,
          }))
        );
        setLoading(false);
      },
      () => {
        setError("Could not load");
        setLoading(false);
      }
    );
  }, [audienceHash]);

  const maxCount = useMemo(() => {
    if (!buckets.length) return 0;
    return Math.max(0, ...buckets.map((b) => b.count));
  }, [buckets]);

  if (loading) {
    return null;
  }

  if (error) {
    return <p className="text-[10px] text-theme-text opacity-40 w-[100px] shrink-0">{error}</p>;
  }

  if (!buckets.length) {
    return null;
  }

  const n = buckets.length;

  return (
    <div className="relative shrink-0 w-[100px]">
      <div className="relative flex items-end gap-px h-9">
        {buckets.map((b, i) => {
          const h = barHeightPx(b.count, maxCount || 1);
          const isHovered = hoveredIndex === i;
          return (
            <div
              key={b.dayMs}
              className="flex-1 min-w-0 h-full flex flex-col justify-end"
              onMouseEnter={() => setHoveredIndex(i)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <div
                className={`rounded-t-sm cursor-pointer transition-opacity bg-theme-text ${
                  isHovered ? "opacity-35" : b.count > 0 ? "opacity-25" : "opacity-10"
                } hover:opacity-40`}
                style={{ height: `${h}px` }}
                role="presentation"
              />
            </div>
          );
        })}
      </div>
      {hoveredIndex != null && buckets[hoveredIndex] != null && (
        <div
          className="absolute z-10 px-2 py-1.5 rounded bg-theme-secondary border border-theme-secondary text-theme-text text-xs shadow-lg pointer-events-none whitespace-nowrap"
          style={{
            left: `${((hoveredIndex + 0.5) / n) * 100}%`,
            transform: "translateX(-50%)",
            top: "100%",
            marginTop: "4px",
          }}
        >
          <div className="font-medium">{formatDayLabel(buckets[hoveredIndex].dayMs)}</div>
          <div className="text-theme-text opacity-80 mt-0.5">
            {buckets[hoveredIndex].count} video{buckets[hoveredIndex].count !== 1 ? "s" : ""}
          </div>
        </div>
      )}
    </div>
  );
};

export default AudienceUploadTimelineHistogram;
